import React, { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { mergeRefs, useLayer } from 'react-laag';
import { PlacementType } from 'react-laag/dist/PlacementType';
import { ColorPickerV2 } from '@bynder/design-system';
import { fixLayerProps, getFirstShadowElement, useId, useKeyDown } from 'packages/ds/ColorPicker/layerHelpers';
import type { PickerValue } from './pickerValue';
import type { TriggerRenderProps } from './shared';

const Z_INDEX = 20000;
const POSSIBLE_PLACEMENTS: PlacementType[] = ['bottom-start', 'top-start', 'bottom-end', 'top-end', 'left-end'];

type Props = {
    value: PickerValue;
    onChange: (value: PickerValue) => void;
    types: readonly ('solid' | 'linear' | 'radial')[];
    trigger: (props: TriggerRenderProps) => ReactNode;
};

/** `ColorPickerV2` hardcodes `bottom-start`, so the one call site that needs `left-end` gets its own layer. */
export function ColorPickerLayer({ value, onChange, types, trigger }: Props) {
    const [isOpen, setIsOpen] = useState(false);
    const layerRef = useRef<HTMLDivElement | null>(null);
    const triggerRef = useRef<HTMLElement | null>(null);
    const dropdownId = useId('colorpicker');

    const {
        renderLayer,
        triggerProps: { ref: triggerLayerRef },
        layerProps,
    } = useLayer({
        isOpen,
        onOutsideClick: undefined,
        triggerOffset: 8,
        containerOffset: 16,
        auto: true,
        container: getFirstShadowElement(triggerRef.current),
        placement: 'bottom-start',
        preferX: 'left',
        preferY: 'bottom',
        possiblePlacements: POSSIBLE_PLACEMENTS,
    });

    fixLayerProps(layerProps);

    const close = useCallback(() => {
        if (isOpen) {
            setIsOpen(false);
        }
    }, [isOpen]);

    useKeyDown('Escape', close);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const handleMouseDown = (event: MouseEvent) => {
            const target = event.target as Node;
            const clickedInside = layerRef.current?.contains(target) || triggerRef.current?.contains(target);

            if (!clickedInside) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleMouseDown);

        return () => {
            document.removeEventListener('mousedown', handleMouseDown);
        };
    }, [isOpen]);

    return (
        <>
            {trigger({
                isOpen,
                onClick: () => setIsOpen((prev) => !prev),
                'aria-haspopup': 'dialog',
                'aria-expanded': isOpen,
                'aria-controls': dropdownId,
                ref: mergeRefs(triggerLayerRef, triggerRef),
            })}

            {isOpen &&
                renderLayer(
                    <Container id={dropdownId} {...layerProps} ref={mergeRefs(layerRef, layerProps.ref)}>
                        <ColorPickerV2 value={value} onChange={onChange} types={types} />
                    </Container>,
                )}
        </>
    );
}

const Container = styled.div`
    z-index: ${Z_INDEX};
`;
